import { ElementRef, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { FlowCreationModal, FlowModals, State, flowActions, tabActions } from '@store/index';

import { Button, IconBadge, Transition } from '@common/index';
import { alpha, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField } from '@mui/material';
import { useWorkflow } from '@common/hooks/useWorkflow';
import { loadTemplate } from './templates';
import { SelectTemplateLayout } from './SelectTemplateLayout';
import { SelectTemplateSidebar } from './SelectTemplateSidebar';
import { SelectTemplateBody } from './SelectTemplateBody';
import { ArchitectureOutlined } from '@mui/icons-material';
import { useTheme } from '../../../styles';
import { Template, TemplatesGetArgs, templatesService, TemplateType } from '@requests/templates';
import { WorkflowData } from '@common/hooks/types';
import { RequestError } from '@requests/common';
import { enqueueSnackbar } from 'notistack';

export const SelectTemplate: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const textBoxRef = useRef<ElementRef<typeof TextField>>(null);

  const { workflow_id: flow_id } = useParams<{ service_id: string; workflow_id: string }>();
  const { loadFlowIntoStore } = useWorkflow();

  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [category, setCategory] = useState<TemplateType>(TemplateType.CHATBOT);

  const {
    data: template,
    isLoading,
    isFetching,
  } = useQuery<Template, RequestError, Template, [string, TemplatesGetArgs]>({
    queryKey: ['template', { id: selectedTemplate as string }],
    queryFn: templatesService.get,
    refetchOnReconnect: true,
    enabled: Boolean(selectedTemplate),
  });

  const username = useSelector((state: State) => state.auth.username);
  const name = useSelector((state: State) => state.tabs.active.label);
  const flowCreationModal = useSelector((state: State) => state.flow.modal) as FlowCreationModal;

  if (flowCreationModal === null) return null;

  const setName = (val: string) => dispatch(tabActions.setTabName(val));
  const modalOpen = flowCreationModal?.type === FlowModals.SelectTemplate;
  const variant = flowCreationModal?.variant;

  const goBack = () => {
    dispatch(flowActions.setFlowModal({ type: FlowModals.SelectNewFlow, variant }));
  };

  const createNewFlow = () => {
    if (!flow_id || !template) return;
    if (!name) {
      textBoxRef.current?.focus();
      return;
    }

    const templateData = template.data as unknown as WorkflowData;

    let loadedTemplate;
    try {
      loadedTemplate = loadTemplate(templateData, flow_id, name, username);
    } catch (error) {
      enqueueSnackbar('Failed to load template!', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }

    if (loadedTemplate) {
      loadFlowIntoStore(loadedTemplate);
      queryClient.invalidateQueries({ queryKey: ['flow_info'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      dispatch(tabActions.setSaved(false));
      closeModal();
    }
  };

  const closeModal = () => dispatch(flowActions.setFlowModal(null));
  const templateLoading = isLoading || isFetching;

  return (
    <Dialog
      data-intercom-target='Modal Overlay'
      TransitionComponent={Transition}
      open={modalOpen}
      fullWidth
      onClose={goBack}
      PaperProps={{
        style: {
          backgroundColor: '#0f141b',
          minWidth: '80%',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 32px' }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <IconBadge background={alpha(theme.text.standard, 0.16)}>
            <ArchitectureOutlined />
          </IconBadge>
          <h3>Select a Template</h3>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: 0 }}>
        <SelectTemplateLayout
          body={
            <SelectTemplateBody
              category={category}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
            />
          }
          sidebar={
            <SelectTemplateSidebar
              name={name}
              setName={setName}
              selectedTemplate={selectedTemplate}
              category={category}
              setCategory={setCategory}
            />
          }
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ padding: '24px 32px' }}>
        <Stack direction='row' spacing={1}>
          <Button small onClick={goBack}>
            Back
          </Button>
          <Button
            small
            customColor='primary'
            onClick={createNewFlow}
            disabled={!selectedTemplate || !name || templateLoading}
            data-intercom-target='workflow-create-button'
          >
            Create
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
